<template>
  <div class="ct-Page">
    <CheckinsSettingsToolbar>
      <template #action>
        <AppButton
          v-if="!isEmptyTemplates"
          height="24"
          icon="plus-next"
          type="primary-next"
          width="24"
          @click="createTemplate"
        >
          {{ t('schedule.create_template') }}
        </AppButton>
      </template>
    </CheckinsSettingsToolbar>

    <div class="ct-Page_Content">
      <SettingsPagesEmptyState
        v-if="isEmptyTemplates"
        hero-height="160"
        hero-icon="no-templates-hero"
        hero-width="142"
      >
        <template #title> {{ $t('schedule.no_templates_yet') }} </template>
        <template #subtitle> {{ $t('schedule.dont_have_templates') }} </template>

        <template #action>
          <AppButton
            height="24"
            icon="plus-next"
            type="primary-next"
            width="24"
            @click="createTemplate"
          >
            {{ $t('schedule.create_template') }}
          </AppButton>
        </template>
      </SettingsPagesEmptyState>
      <AppTable
        v-if="!isEmptyTemplates"
        :columns="TABLE_COLUMNS"
        :data="templatesData"
        :disable-user-select="false"
        :hover-row="tableHoverRow"
        :loading="loaders.table"
        border-on-last-row
        no-border
        offset-left="var(--page-left-padding)"
        offset-right="var(--page-right-padding)"
        type="primary-next"
      >
        <template #loading>
          <CheckinsTemplatesTableLoader />
        </template>

        <template #cell="{ columnKey, item, index }">
          <template v-if="columnKey === NAME">
            <div class="ct-TemplateName">
              <div class="ct-TemplateName_Icon">
                {{ item[EMOJI] }}
              </div>

              <span class="oboard-truncated-text">{{ item[TITLE] }}</span>
            </div>
          </template>

          <template v-if="columnKey === WORKSPACE">
            <WorkspacesListCell :limit="3" :workspaces="item[WORKSPACES]" />
          </template>
          <template v-if="columnKey === ACTION">
            <DropdownMenu
              :items="MENU_TEMPLATE_ACTIONS"
              :offset="[0, 0]"
              :type="DROPDOWN_MENU_TYPES.DEFAULT_NEXT"
              position="bottom-end"
              @close="tableHoverRow = -1"
              @open="tableHoverRow = index"
              @item-click="onMenuTemplateActionsClick($event, item)"
            >
              <template #activator="{ isOpened }">
                <AppButton
                  :class="{ 'ct-ActionsButton-active': isOpened }"
                  class="ct-ActionsButton"
                  icon="more-next"
                  size="sm"
                  type="tertiary-next"
                />
              </template>

              <template #item-label="{ item: labelItem }">
                <DropdownMenuItemLabelWithDescription :label-item="labelItem" />
              </template>
            </DropdownMenu>
          </template>
        </template>
        <template #footer>
          <AppTableCreateButton full-width icon-name="plus-next" @click="createTemplate">
            {{ $t('schedule.create_template') }}
          </AppTableCreateButton>
        </template>
      </AppTable>
    </div>
  </div>

  <portal to="modal-windows">
    <CheckinTemplateModal
      :show="isShowTemplateModal"
      :template-id="templateIdForEdit"
      @on-close="closeTemplateModal"
      @template-saved="onReloadTemplates"
    />

    <AppDeleteModal
      :keyword="KEYWORD"
      :loading="loaders.deleteTemplate"
      :show="isShowTemplateDeleteModal"
      :title="$t('schedule.delete_template.title')"
      keypath="schedule.delete_template.confirm_message"
      @on-close="onDeleteTemplateModalClose"
      @on-confirm="onDeleteTemplateConfirmed"
    >
      <template #i18n-t:templateName>
        {{ templateForDelete[CHECKIN_TEMPLATE_ENTITY_KEYS.TITLE] }}
      </template>
    </AppDeleteModal>
  </portal>
</template>

<script setup>
import { isEmpty } from 'lodash'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import CheckinTemplatesApiHandler from '@/api/checkin-templates'
import { ACTIONS_KEYS } from '@/utils/actions-keys'
import { DROPDOWN_MENU_TYPES } from '@/utils/components-configurations/dropdown-menu'
import { MENU_ITEMS_GROUPS } from '@/utils/dropdown-menu'
import { CHECKIN_TEMPLATE_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { KEYWORD } from '@/utils/helpers'
import { TABLE_COLUMNS_KEYS } from '@/utils/table-columns'

import CheckinTemplateModal from '@/components/check-ins/CheckinTemplateModal'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppDeleteModal from '@/components/ui/AppDeleteModal/AppDeleteModal'
import AppTableCreateButton from '@/components/ui/AppTableCreateButton'
import DropdownMenu from '@/components/ui/DropdownMenu/DropdownMenu'
import DropdownMenuItemLabelWithDescription from '@/components/ui/DropdownMenu/DropdownMenuItemLabelWithDescription'
import SettingsPagesEmptyState from '@/components/ui/SettingsPagesEmptyState/SettingsPagesEmptyState'
import CheckinsTemplatesTableLoader from '@/components/ui/SkeletonLoaders/CheckinsTemplatesTableLoader'
import AppTable from '@/components/ui/Table/AppTable'
import WorkspacesListCell from '@/components/ui/WorkspacesListCell/WorkspacesListCell'
import CheckinsSettingsToolbar from '@/views/workspaces/settings/CheckinsSettingsToolbar'

defineOptions({
  name: 'CheckInsTemplatesLayout'
})
const { t } = useI18n()

const { NAME, WORKSPACE, ACTION } = TABLE_COLUMNS_KEYS
const { EMOJI, TITLE, WORKSPACES } = CHECKIN_TEMPLATE_ENTITY_KEYS

const tableHoverRow = ref(-1)

const loaders = ref({
  table: false,
  deleteTemplate: false
})

onMounted(async () => {
  await fetchTemplates()
})

const templateForDelete = ref({})
const isShowTemplateDeleteModal = ref(false)

const onDeleteTemplateModalClose = () => {
  isShowTemplateDeleteModal.value = false
  templateForDelete.value = {}
}

const onDeleteTemplateConfirmed = async () => {
  loaders.value.deleteTemplate = true
  const api = new CheckinTemplatesApiHandler()

  try {
    const { id } = templateForDelete.value
    await api.deleteTemplate(id)
    onDeleteTemplateModalClose()
    await onReloadTemplates()
  } catch (error) {
    handleError({ error })
  } finally {
    loaders.value.deleteTemplate = false
  }
}

const onDeleteTemplate = async template => {
  templateForDelete.value = template
  await nextTick()
  isShowTemplateDeleteModal.value = true
}

const MENU_TEMPLATE_ACTIONS = [
  {
    name: ACTIONS_KEYS.EDIT,
    title: 'action.edit',
    icon: 'edit-next',
    group: MENU_ITEMS_GROUPS.EDITING
  },
  {
    name: ACTIONS_KEYS.REMOVE,
    title: 'action.delete',
    icon: 'delete-next',
    color: 'var(--grade-low-color-next)',
    group: MENU_ITEMS_GROUPS.REMOVING
  }
]

const templatesData = ref([])
const TABLE_COLUMNS = [
  {
    title: t('checkins.template_name'),
    key: NAME,
    width: 'auto'
  },
  {
    title: t('workspaces.workspaces'),
    key: WORKSPACE,
    width: 160
  },
  {
    key: ACTION,
    width: 24
  }
]

const isEmptyTemplates = computed(() => {
  return !loaders.value.table && isEmpty(templatesData.value)
})

const isShowTemplateModal = ref(false)
const createTemplate = () => {
  isShowTemplateModal.value = true
}

const templateIdForEdit = ref(null)
const onEditTemplate = id => {
  templateIdForEdit.value = id
  isShowTemplateModal.value = true
}
const onMenuTemplateActionsClick = (name, item) => {
  if (name === ACTIONS_KEYS.EDIT) {
    onEditTemplate(item[CHECKIN_TEMPLATE_ENTITY_KEYS.ID])
  }

  if (name === ACTIONS_KEYS.REMOVE) {
    onDeleteTemplate(item)
  }
}

const closeTemplateModal = () => {
  templateIdForEdit.value = null
  isShowTemplateModal.value = false
}

const fetchTemplates = async () => {
  try {
    loaders.value.table = true
    const api = new CheckinTemplatesApiHandler()
    templatesData.value = await api.getTemplates()
  } catch (error) {
    handleError({ error })
  } finally {
    loaders.value.table = false
  }
}
const onReloadTemplates = async () => {
  await fetchTemplates()
}
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/app-table-helpers';

.ct-Page_Content {
  width: 100%;
  max-width: var(--ob-automations-page-width);
  padding: 0 $page-right-padding 0 $page-left-padding;
  gap: 24px;
}

.ct-ActionsButton {
  &-active {
    background-color: $dark-2;
    color: $white;
  }
  @extend %app-table-hidden-items;
}

.ct-TemplateName {
  font-weight: fw('semi-bold');
  display: flex;
  align-items: center;
  padding-right: 8px;
}
.ct-TemplateName_Icon {
  width: 24px;
  text-align: center;
  flex-shrink: 0;
}
</style>
